// **検証用の機能をまとめる入口。** このリポジトリは研究・調査用なので画面に全部載せる
// （2026-10-09・ユーザー。DOC-0026 §1）。
//
// **本番リポジトリではここごと外す。** そのために
//   - 画面の組み立てもこの中でやる（`index.html` に置くのは空の入れ物 1 つだけ）
//   - `main.ts` からの取り込みは `mountDiagnostics` の 1 本だけ
// にしてある。外すときは `main.ts` の import 1 行と呼び出し 1 行、`index.html` の 1 要素を消す。
//
// いま入っている試験:
//   1. **Worker 対応**（#52 ①）— WebTransport を Worker に移せるか。通話とは別の接続を 1 本張る
//   2. **ブラウザのノイズ抑制**（#26）— 3 条件で録って、ブラウザの音声処理の効きを測る

import { diffOf, formatDiff, formatPassResult, runBrowserNsProbe, SECONDS, PASSES } from "./browser-ns.ts";
import { formatProbeResult, probeWorkerTransport } from "./worker-probe.ts";

export interface DiagnosticsHost {
  /** 画面と保存ログに 1 行出す（`main.ts` の `log`）。 */
  log: (level: "info" | "warn" | "error", text: string) => void;
  /** 接続先の URL。Worker 対応の試験が使う。 */
  getUrl: () => string;
  /** **通話中か。** 通話中はマイクを使う試験を断る（マイクの取り合いになるため）。 */
  isInCall: () => boolean;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (text !== undefined) e.textContent = text;
  return e;
}

/** ボタン 1 つを `div` で包む（並びを縦にするため）。 */
function row(child: HTMLElement): HTMLDivElement {
  const div = el("div");
  div.append(child);
  return div;
}

/**
 * 説明文。**中に `<strong>` や `<code>` を入れたいので `innerHTML` を使う。**
 * 渡すのはこのファイル内の固定文だけ（外から来た文字列は入れない）。
 */
function note(html: string): HTMLParagraphElement {
  const p = el("p");
  const small = el("small");
  small.innerHTML = html;
  p.append(small);
  return p;
}

function stamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
}

/** 検証用の画面を `container` の中に作る。**呼び出しはここ 1 回だけ**。 */
export function mountDiagnostics(container: HTMLElement, host: DiagnosticsHost): void {
  container.textContent = "";

  // ---- ① Worker 対応 -------------------------------------------------
  const workerButton = el("button", "Worker 対応を確かめる");
  workerButton.type = "button";
  const workerNote = note(
    "上の URL へ<strong>通話とは別の接続を 1 本</strong>張り、Worker（画面と別のスレッド）の中で " +
      "WebTransport が使えるかを調べます（#52 ①）。<strong>入室しないので通話中の部屋には影響しません</strong>。",
  );

  workerButton.addEventListener("click", () => {
    const url = host.getUrl();
    if (!url) {
      host.log("warn", "Worker 試験: 先に接続先の URL を入れる");
      return;
    }
    workerButton.disabled = true;
    host.log("info", `Worker 試験: 開始（${url}。入室はしない）`);
    void probeWorkerTransport(url)
      .then((result) => {
        for (const line of formatProbeResult(result)) {
          host.log(result.error === null ? "info" : "warn", line);
        }
      })
      .catch((e: unknown) => host.log("error", `Worker 試験が失敗しました: ${String(e)}`))
      .finally(() => {
        workerButton.disabled = false;
      });
  });

  // ---- ② ブラウザのノイズ抑制 ---------------------------------------
  const nsButton = el("button", "ブラウザのノイズ抑制を測る");
  nsButton.type = "button";
  const phase = el("div", "—");
  phase.className = "diag-phase";
  const files = el("div");
  files.className = "diag-files";
  const nsNote = note(
    `<strong>通話はしません。</strong>${PASSES.length} 通りの条件で ${SECONDS} 秒ずつ録り、` +
      "ブラウザの音声処理だけの効きを測ります（#26）。" +
      "<strong>騒音のある場所で、前半は黙り、後半は毎回同じ文を読んでください。</strong>" +
      "終わったら wav を保存し、<code>src/</code> に置いて <code>research の poc/denoise</code> の " +
      "<code>p27-room-recordings</code> で測り直します。" +
      "<strong>イヤホンで測ってください</strong>（条件 C はエコーキャンセルが切れているので、" +
      "スピーカーだと自分の声が回り込みます）。",
  );

  nsButton.addEventListener("click", () => {
    if (host.isInCall()) {
      host.log("warn", "ブラウザ抑制: 通話中は測れない（マイクの取り合いになる）。退室してから押す");
      return;
    }
    nsButton.disabled = true;
    files.textContent = "";
    const tag = stamp();
    host.log("info", `ブラウザ抑制: 開始（${PASSES.length} 条件 × ${SECONDS} 秒。通話はしない）`);
    host.log("info", `ブラウザ抑制: ${navigator.userAgent}`);
    const results: Parameters<typeof diffOf>[0] = [];
    void runBrowserNsProbe({
      onPhase: (text) => {
        phase.textContent = text;
      },
      onResult: (result) => {
        results.push(result);
        host.log("info", formatPassResult(result));
        const a = el("a", `⬇ ns-${tag}-${result.pass.key}.wav`);
        a.href = URL.createObjectURL(result.wav);
        a.download = `ns-${tag}-${result.pass.key}.wav`;
        files.append(a);
      },
    })
      .then(() => {
        for (const line of formatDiff(diffOf(results))) host.log("info", line);
        phase.textContent = "終わり — wav を保存して、ログも保存する";
      })
      .catch((e: unknown) => {
        host.log("error", `ブラウザ抑制が失敗しました: ${String(e)}`);
        phase.textContent = "失敗しました（ログを見る）";
      })
      .finally(() => {
        nsButton.disabled = false;
      });
  });

  container.append(row(workerButton), workerNote, row(nsButton), nsNote, phase, files);
}
